import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { Rally, SpottedEvent, ResultsResponse, PlayerStats } from '@/api/resultsApi';
import VideoPlayer, { type VideoPlayerHandle } from './VideoPlayer';
import RallyTimeline from './RallyTimeline';
import PlayerCard from './PlayerCard';
import HeatmapOverlay from './HeatmapOverlay';

interface MatchViewerProps {
  results: ResultsResponse | null;
  videoUrl: string;
  isLoading: boolean;
  error: string | null;
  onRetry?: () => void;
}

/**
 * Determina la squadra più probabile per ogni giocatore in base agli eventi.
 * Restituisce un record player_id -> 'A' | 'B' | undefined.
 */
function inferTeams(rallies: Rally[]): Record<number, 'A' | 'B'> {
  const counts: Record<number, Record<'A' | 'B', number>> = {};
  for (const r of rallies) {
    for (const ev of r.events) {
      if (!counts[ev.player_id]) counts[ev.player_id] = { A: 0, B: 0 };
      if (ev.team === 'A' || ev.team === 'B') {
        counts[ev.player_id][ev.team]++;
      }
    }
  }
  const teams: Record<number, 'A' | 'B'> = {};
  for (const [pid, cnt] of Object.entries(counts)) {
    teams[Number(pid)] = cnt.A >= cnt.B ? 'A' : 'B';
  }
  return teams;
}

/**
 * Estrae i punti per la heatmap dalle coordinate degli eventi di attacco.
 */
function getAttackHeatmapPoints(rallies: Rally[]) {
  const points: { x: number; y: number; confidence: number }[] = [];
  for (const r of rallies) {
    for (const ev of r.events) {
      if (ev.type === 'attack' || ev.type === 'tip') {
        points.push({
          x: ev.spatial_location.x,
          y: ev.spatial_location.y,
          confidence: ev.confidence,
        });
      }
    }
  }
  return points;
}

/**
 * Vista principale per l'analisi di una partita.
 * Combina player video, timeline interattiva, statistiche giocatori e heatmap.
 */
const MatchViewer: React.FC<MatchViewerProps> = ({
  results,
  videoUrl,
  isLoading,
  error,
  onRetry,
}) => {
  const playerRef = useRef<VideoPlayerHandle>(null);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [selectedRallyId, setSelectedRallyId] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);

  // Dati derivati
  const rallies: Rally[] = results?.rallies ?? [];
  const playerStats: Record<number, PlayerStats> = results?.stats?.player_stats ?? {};
  const playerTeams = useMemo(() => inferTeams(rallies), [rallies]);
  const heatmapPoints = useMemo(() => getAttackHeatmapPoints(rallies), [rallies]);
  // Dimensioni per heatmap: useremo un valore fisso o, in futuro, le dimensioni reali.
  // Qui assumiamo 1920x1080 come riferimento scalato in base al player.
  const heatmapWidth = 1920;
  const heatmapHeight = 1080;

  const handleProgress = useCallback((ms: number) => {
    setCurrentTimeMs(ms);
  }, []);

  const handleDuration = useCallback((ms: number) => {
    setDurationMs(ms);
  }, []);

  const handleRallyClick = useCallback((rally: Rally) => {
    if (playerRef.current) {
      playerRef.current.seekTo(rally.start_timestamp_ms);
      setSelectedRallyId(rally.rally_id);
      setSelectedEventId(null);
    }
  }, []);

  const handleEventClick = useCallback((event: SpottedEvent) => {
    if (playerRef.current) {
      playerRef.current.seekTo(event.timestamp_ms);
      setSelectedEventId(event.event_id);
      setSelectedRallyId(null);
    }
  }, []);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <span>Caricamento partita...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#b91c1c' }}>
        <p>Errore nel caricamento dei risultati: {error}</p>
        {onRetry && (
          <button onClick={onRetry} style={{ marginTop: '0.5rem', padding: '0.5rem 1rem', cursor: 'pointer' }}>
            Riprova
          </button>
        )}
      </div>
    );
  }

  if (!results || rallies.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
        Nessun risultato ancora disponibile. Il video potrebbe essere ancora in elaborazione.
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Player + heatmap */}
      <div style={{ position: 'relative' }}>
        <VideoPlayer
          ref={playerRef}
          src={videoUrl}
          onProgress={handleProgress}
          onDuration={handleDuration}
        />
        {heatmapPoints.length > 0 && (
          <HeatmapOverlay
            points={heatmapPoints}
            width={heatmapWidth}
            height={heatmapHeight}
          />
        )}
      </div>

      {/* Timeline dei rally */}
      <RallyTimeline
        rallies={rallies}
        durationMs={durationMs}
        currentTimeMs={currentTimeMs}
        onRallyClick={handleRallyClick}
        onEventClick={handleEventClick}
      />

      {/* Statistiche giocatori */}
      <div>
        <h3 style={{ margin: '0 0 0.5rem 0', color: '#1e293b' }}>Statistiche Giocatori</h3>
        {Object.keys(playerStats).length === 0 ? (
          <p style={{ color: '#64748b' }}>Nessuna statistica disponibile.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '1rem' }}>
            {Object.entries(playerStats).map(([playerId, stats]) => {
              const pid = Number(playerId);
              const team = playerTeams[pid];
              return (
                <PlayerCard
                  key={pid}
                  playerId={pid}
                  stats={stats}
                  team={team}
                  isHighlighted={selectedEventId !== null || selectedRallyId !== null}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Punteggio finale (opzionale) */}
      {rallies.length > 0 && rallies[rallies.length - 1].score && (
        <div style={{ textAlign: 'center', marginTop: '1rem', color: '#475569' }}>
          Punteggio finale: {rallies[rallies.length - 1].score.A} - {rallies[rallies.length - 1].score.B}
        </div>
      )}
    </div>
  );
};

export default MatchViewer;